import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { BookButton } from "@/components/site/BookButton";
import { Seo } from "@/components/site/Seo";

const IMG = {
  hero: "https://images.unsplash.com/photo-1651663303138-4dc283e15992?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  calm: "https://images.unsplash.com/photo-1548882656-7aa2809d8a13?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  newborn: "https://images.unsplash.com/photo-1764747415171-4a0cb6b98dde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

const BENEFITS = [
  ["A balanced, aligned pelvis", "Gentle, pregnancy-specific care (including the Webster Technique) focuses on the alignment and mobility of the sacrum and pelvis, supporting balanced muscle and ligament tension around the uterus."],
  ["Less pain through the journey", "Many expectant mothers seek care for low-back, pelvic and round-ligament discomfort. Supporting joint motion and soft tissue can help you stay more comfortable as your body changes."],
  ["Calmer nervous system for mom & baby", "Pregnancy is a demanding load on the autonomic nervous system. Gentle care encourages the calming 'rest and recover' state — and a less-stressed mom is a gift to a developing baby."],
  ["Better posture as you grow", "As the center of gravity shifts, posture adapts fast. Care helps manage those postural changes to reduce strain and help prevent compensations that linger postpartum."],
  ["Room for baby to move", "Balanced pelvic biomechanics may give baby optimal room to assume a favorable position for birth — a key idea behind the Webster Technique."],
  ["Support toward an easier birth", "A frequently cited study observed shorter labor times among mothers receiving chiropractic care during pregnancy. Research is still evolving, and every birth is unique — but reducing physical stress and improving pelvic function is a sensible, well-tolerated goal."],
];

export default function Pregnancy() {
  return (
    <div>
      <Seo title="Pregnancy Chiropractor in Gardner, KS | Webster Technique | Psalm 30:2" description="Gentle, nervous-system-focused pregnancy chiropractic in Gardner, Kansas. Support a balanced pelvis, reduce pain and stress for mom and baby, improve posture, and prepare the body for birth." />
      <PageHero
        eyebrow="Pregnancy & Birth"
        title={<>A calmer body.<br />A supported birth.</>}
        intro="Pregnancy asks an enormous amount of your body and your nervous system. Our gentle, pregnancy-specific care supports a balanced pelvis, reduces physical stress for mom and baby, and helps prepare your body for the work of birth."
        image={IMG.hero}
      />

      {/* Why NS in pregnancy */}
      <section className="bg-ivory py-24 lg:py-32 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-5">
            <div className="overline mb-6">Mom & baby, one system</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05] text-espresso">Less stress on the body means less stress on birth.</h2>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7 space-y-6 text-lg font-light leading-relaxed text-espressosoft/85">
            <p>As your body changes, the pelvis, spine and surrounding ligaments are under constant, shifting load. The nervous system is working hard to keep you stable, comfortable and regulated through it all.</p>
            <p>When the pelvis moves and balances well, muscles and ligaments can share tension evenly, and the autonomic nervous system can spend more time in its calm, restorative state. That benefits you — and the environment your baby is growing in.</p>
            <p>Care is always gentle, unhurried and adapted to your stage of pregnancy, using specialized positioning and pregnancy pillows for your comfort.</p>
          </Reveal>
        </div>
      </section>

      {/* Benefits grid */}
      <section className="bg-linen py-24 lg:py-32 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="overline mb-5">How care may help</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl text-espresso max-w-2xl">Supporting you through every trimester.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10 mt-14">
            {BENEFITS.map(([t, d], i) => (
              <Reveal key={t} delay={(i % 2) * 80}>
                <div className="flex gap-4 border-t border-espresso/15 pt-6">
                  <Check className="text-sage shrink-0 mt-1" strokeWidth={1.6} />
                  <div>
                    <h3 className="font-serif text-2xl text-espresso mb-2">{t}</h3>
                    <p className="text-espressosoft/80 font-light leading-relaxed">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Postpartum */}
      <section className="bg-ivory py-24 lg:py-32 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <div className="overflow-hidden rounded-sm aspect-[4/3]">
              <img src={IMG.newborn} alt="Postpartum recovery and newborn care" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-6">
            <div className="overline mb-5">After birth</div>
            <h2 className="font-serif font-light text-4xl lg:text-5xl text-espresso mb-6">Postpartum recovery, gently.</h2>
            <p className="text-lg font-light leading-relaxed text-espressosoft/85 mb-4">Birth is a major event for the whole system. We support your recovery as your body rebuilds — addressing posture, pelvic function and the aches that come with feeding and carrying a newborn.</p>
            <p className="text-lg font-light leading-relaxed text-espressosoft/85">And when you're ready, we offer very gentle care for your newborn too. <Link to="/pediatric" className="link-underline text-espresso">Learn about pediatric care →</Link></p>
          </Reveal>
        </div>
      </section>

      <section className="bg-linen py-20 lg:py-28 px-6 md:px-10 lg:px-14">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="font-serif font-light text-2xl lg:text-3xl leading-snug text-espresso mb-6">Every pregnancy and every birth is unique.</p>
            <p className="text-sm text-taupe max-w-xl mx-auto mb-10">This page is educational and does not replace the guidance of your OB, midwife or care team, and is not a guarantee of any birth outcome. We coordinate with your providers and tailor all care to you.</p>
            <BookButton label="Book Pregnancy Care" variant="primary" track_label="pregnancy" testid="pregnancy-book-button" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
